// Cart data interfaces
import { Program } from "@/common/program/types";
import { EnrichedProgramInfo } from "@/lib/ecommerce/commercetools/cart";

export interface CartItem {
  id: string; // uuid
  courseKey?: string; // e.g. "GTx+CS1301xIII"
  courseRunKey?: string; // e.g. "course-v1:GTx+CS1301xIII+1T2025"
  courseUuid?: string; // e.g. "7c566601-6ddb-4013-aa8e-e0cf29482e35"
  courseName: string;
  price: number;
  quantity: number;
  originalPrice?: number;
  totalPrice?: number; // Total price for this item (price * quantity)
  discount?: number;
  type?: string;
  sku?: string;
  brand?: string; // Brand from CommerceTools variant.attributes.brand-text
  bundleId?: string; // Bundle ID for programs (distinguishes from courses)
  image?: {
    url: string;
  };
}

export interface Discount {
  type: "percentage" | "fixed";
  value: number;
  description: string;
}

export interface BundleInfoMap {
  [key: string]: Program;
}

export interface CartData {
  items: CartItem[];
  subtotal: number;
  discounts: Discount[];
  total: number;
  tax: number;
  shipping: number[];
  enrichedProgramInfo: {
    [bundleId: string]: EnrichedProgramInfo;
  };
  bundleInfoMap: BundleInfoMap;
}

// Format currency helper function
export function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

// Function to get dynamic cart data from localStorage or cart state
export function getDynamicCartData(): CartData {
  // Return empty cart as fallback when BFF is unavailable
  // This ensures we don't show stale/static data
  return {
    items: [],
    subtotal: 0,
    discounts: [],
    total: 0,
    tax: 0,
    shipping: [],
    enrichedProgramInfo: {},
    bundleInfoMap: {},
  };
}

// Helper function to add items to cart for testing
export function addToCart(item: CartItem) {
  if (typeof window === "undefined") return;

  try {
    const existingItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const existingItem = existingItems.find((i: CartItem) => i.id === item.id);

    if (existingItem) {
      existingItem.quantity += item.quantity || 1;
    } else {
      existingItems.push(item);
    }

    localStorage.setItem("cartItems", JSON.stringify(existingItems));

    // Trigger a custom event to notify the cart component
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  } catch (error) {
    console.error("Error adding to cart:", error);
  }
}

// Helper function to add discounts to cart for testing
export function addDiscount(discount: Discount) {
  if (typeof window === "undefined") return;

  try {
    const existingDiscounts = JSON.parse(
      localStorage.getItem("cartDiscounts") || "[]",
    );
    existingDiscounts.push(discount);
    localStorage.setItem("cartDiscounts", JSON.stringify(existingDiscounts));

    // Trigger a custom event to notify the cart component
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  } catch (error) {
    console.error("Error adding discount:", error);
  }
}

// Helper function to remove item from cart
export function removeFromCart(itemId: string) {
  if (typeof window === "undefined") return;

  try {
    const existingItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const updatedItems = existingItems.filter(
      (item: CartItem) => item.id !== itemId,
    );
    localStorage.setItem("cartItems", JSON.stringify(updatedItems));

    // Trigger a custom event to notify the cart component
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  } catch (error) {
    console.error("Error removing from cart:", error);
  }
}

// Helper function to clear cart
export function clearCart() {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem("cartItems");
    localStorage.removeItem("cartDiscounts");

    // Trigger a custom event to notify the cart component
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  } catch (error) {
    console.error("Error clearing cart:", error);
  }
}

/**
 * @deprecated We no longer have single program carts (in theory)
 * @param cart
 */
export function firstEnrichedProgramInfo(
  cart: CartData,
): EnrichedProgramInfo | null {
  const items = cart.items
    .filter((i) => i.bundleId)
    .map((i) => cart.enrichedProgramInfo[i.bundleId ?? ""]);

  if (items.length > 0) {
    return items[0];
  }

  return null;
}
/**
 * Fetch the right program infor for a givne CartItem or LineItem Index
 */
export function getProgramInfoForItem(
  cart: CartData,
  item: CartItem | number,
): EnrichedProgramInfo | null {
  let cartItem = null;
  if (typeof item === "number") {
    cartItem = cart.items[item];
  } else {
    cartItem = item;
  }

  return cart.enrichedProgramInfo[cartItem.bundleId ?? ""] || null;
}
